'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Medication {
  id: string;
  name: string;
  dosage: string;
  schedule: string;
  lastTaken: string | null;
  nextDose: string;
  refillDate: string;
  refillsRemaining: number;
  prescribedBy: string;
  instructions: string;
  interactions: string[];
  imageUrl: string;
  imageAlt: string;
}

interface MedicationCardProps {
  medication: Medication;
  onLog: (id: string) => void;
  onViewInteractions: (id: string) => void;
  onRefill: (id: string) => void;
}

export default function MedicationCard({
  medication,
  onLog,
  onViewInteractions,
  onRefill,
}: MedicationCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLogging, setIsLogging] = useState(false);

  const handleLog = () => {
    setIsLogging(true);
    onLog(medication.id);
    setTimeout(() => setIsLogging(false), 1000);
  };

  const needsRefill = medication.refillsRemaining <= 1;
  const hasInteractions = medication.interactions.length > 0;

  return (
    <div className="glass rounded-lg hover:shadow-warm transition-smooth overflow-hidden">
      <div className="p-4 lg:p-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-sage-50 flex-shrink-0">
            <AppImage
              src={medication.imageUrl}
              alt={medication.imageAlt}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-1">
                  {medication.name}
                </h3>
                <p className="text-sm font-caption text-muted-foreground">
                  {medication.dosage} • {medication.schedule}
                </p>
              </div>

              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 rounded-lg hover:bg-muted transition-smooth flex-shrink-0"
                aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
              >
                <Icon
                  name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={20}
                  className="text-muted-foreground"
                />
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-3">
              {medication.lastTaken ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success/10 text-success text-xs font-caption font-medium">
                  <Icon name="CheckCircleIcon" size={14} variant="solid" />
                  Last taken {medication.lastTaken}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-warning/10 text-warning text-xs font-caption font-medium">
                  <Icon name="ClockIcon" size={14} />
                  Not taken today
                </span>
              )}

              {needsRefill && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-error/10 text-error text-xs font-caption font-medium">
                  <Icon name="ExclamationTriangleIcon" size={14} variant="solid" />
                  Refill needed
                </span>
              )}

              {hasInteractions && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-caption font-medium">
                  <Icon name="InformationCircleIcon" size={14} variant="solid" />
                  {medication.interactions.length} interaction
                  {medication.interactions.length > 1 ? 's' : ''}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleLog}
                disabled={isLogging}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-caption font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon
                  name={isLogging ? 'CheckIcon' : 'ClipboardDocumentCheckIcon'}
                  size={16}
                />
                {isLogging ? 'Logged!' : 'Log Dose'}
              </button>

              {hasInteractions && (
                <button
                  onClick={() => onViewInteractions(medication.id)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary/10 text-secondary text-sm font-caption font-medium hover:bg-secondary/20 transition-smooth"
                >
                  <Icon name="ExclamationCircleIcon" size={16} />
                  View Warnings
                </button>
              )}

              {needsRefill && (
                <button
                  onClick={() => onRefill(medication.id)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent/10 text-accent text-sm font-caption font-medium hover:bg-accent/20 transition-smooth"
                >
                  <Icon name="ArrowPathIcon" size={16} />
                  Request Refill
                </button>
              )}
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-sage-200/30 space-y-3 animate-slide-in-up">
            <div>
              <p className="text-xs font-caption text-muted-foreground mb-1">
                Prescribed By
              </p>
              <p className="text-sm font-caption text-foreground">
                {medication.prescribedBy}
              </p>
            </div>

            <div>
              <p className="text-xs font-caption text-muted-foreground mb-1">
                Instructions
              </p>
              <p className="text-sm font-caption text-foreground">
                {medication.instructions}
              </p>
            </div>

            <div>
              <p className="text-xs font-caption text-muted-foreground mb-1">
                Next Dose
              </p>
              <p className="text-sm font-caption text-foreground">
                {medication.nextDose}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-caption text-muted-foreground mb-1">
                  Refill Date
                </p>
                <p className="text-sm font-caption text-foreground">
                  {medication.refillDate}
                </p>
              </div>
              <div>
                <p className="text-xs font-caption text-muted-foreground mb-1">
                  Refills Remaining
                </p>
                <p className="text-sm font-caption text-foreground">
                  {medication.refillsRemaining}
                </p>
              </div>
            </div>

            {hasInteractions && (
              <div>
                <p className="text-xs font-caption text-muted-foreground mb-2">
                  Known Interactions
                </p>
                <ul className="space-y-1">
                  {medication.interactions.map((interaction, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-2 text-sm font-caption text-foreground"
                    >
                      <Icon
                        name="ExclamationCircleIcon"
                        size={16}
                        className="text-secondary flex-shrink-0 mt-0.5"
                      />
                      <span>{interaction}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}